import type { LengthDisplayUnit, WeightDisplayUnit } from '~/models/settings'
import type { CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import type { DisplayString } from '~/types/display'
import type { Centimetres, Kilograms, KilogramsDelta, Percent } from '~/types/units'
import { centimetresToInches, kilogramsToPounds, unwrapNumber } from '~/types/units'

/**
 * The presentation boundary (D-041).
 *
 * Rounding, display-unit conversion, and locale formatting happen here and
 * nowhere else. Everything returns `DisplayString`, which no calculation or
 * rule accepts — so a formatted value cannot leak back into the domain.
 *
 * Canonical storage is unaffected: kilograms and centimetres regardless of what
 * is shown (D-017).
 */

const WEIGHT_DECIMALS = 1
const LENGTH_DECIMALS = 1

export function formatWeight(value: Kilograms, unit: WeightDisplayUnit): DisplayString {
  const converted = unit === 'kg' ? unwrapNumber(value) : unwrapNumber(kilogramsToPounds(value))
  return `${converted.toFixed(WEIGHT_DECIMALS)} ${unit}` as DisplayString
}

/** The numeric part only, for pre-filling an input the user will edit. */
export function formatWeightValue(value: Kilograms, unit: WeightDisplayUnit): DisplayString {
  const converted = unit === 'kg' ? unwrapNumber(value) : unwrapNumber(kilogramsToPounds(value))
  return converted.toFixed(WEIGHT_DECIMALS) as DisplayString
}

export function formatLength(value: Centimetres, unit: LengthDisplayUnit): DisplayString {
  const converted = unit === 'cm' ? unwrapNumber(value) : unwrapNumber(centimetresToInches(value))
  return `${converted.toFixed(LENGTH_DECIMALS)} ${unit}` as DisplayString
}

/** The numeric part only, for pre-filling an input the user will edit. */
export function formatLengthValue(value: Centimetres, unit: LengthDisplayUnit): DisplayString {
  const converted = unit === 'cm' ? unwrapNumber(value) : unwrapNumber(centimetresToInches(value))
  return converted.toFixed(LENGTH_DECIMALS) as DisplayString
}

export function weightUnitLabel(unit: WeightDisplayUnit): DisplayString {
  return unit as DisplayString
}

/**
 * A signed weight change, always carrying its sign.
 *
 * The sign is part of the meaning: "0.4 kg" and "−0.4 kg" are opposite
 * outcomes, and a direction shown only by colour or an arrow would fail the
 * never-colour-alone rule.
 */
export function formatWeightChange(value: KilogramsDelta, unit: WeightDisplayUnit): DisplayString {
  const raw = unwrapNumber(value)
  const converted = unit === 'kg' ? raw : raw / KG_PER_POUND_FOR_DISPLAY
  const sign = converted > 0 ? '+' : converted < 0 ? '−' : ''
  return `${sign}${Math.abs(converted).toFixed(WEIGHT_DECIMALS)} ${unit}` as DisplayString
}

/** A signed percentage, to one decimal place. */
export function formatPercentChange(value: Percent): DisplayString {
  const raw = unwrapNumber(value)
  const sign = raw > 0 ? '+' : raw < 0 ? '−' : ''
  return `${sign}${Math.abs(raw).toFixed(1)}%` as DisplayString
}

/* Local copy of the conversion factor: `format` must not import from
   `calculations`, and the display path is the only consumer. */
const KG_PER_POUND_FOR_DISPLAY = 0.45359237

/**
 * A calendar date in the user's locale.
 *
 * Parsed as UTC and formatted with a UTC time zone so the displayed day always
 * matches the stored day. Passing `'2026-08-06'` to `new Date()` yields UTC
 * midnight, which local formatting would render as the 5th anywhere behind
 * Greenwich.
 */
export function formatCalendarDate(
  date: CalendarDate,
  options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' },
): DisplayString {
  const instant = new Date(`${unwrapDate(date)}T00:00:00Z`)
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' }).format(
    instant,
  ) as DisplayString
}

export function formatDayCount(days: number): DisplayString {
  return `${days} ${days === 1 ? 'day' : 'days'}` as DisplayString
}

/**
 * A projected month — month and year, never a day (D-068).
 *
 * The value that arrives here has no day in it to render, which is deliberate:
 * a projected calendar date reads as a commitment the underlying trend cannot
 * support. `month` is 1–12 and is placed on the 1st only to construct the
 * formatter's input; that day is never shown, because the options ask for the
 * month and the year alone.
 */
export function formatApproximateMonth(value: {
  readonly year: number
  readonly month: number
}): DisplayString {
  const instant = new Date(
    `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-01T00:00:00Z`,
  )
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(instant) as DisplayString
}

/**
 * A span of whole weeks.
 *
 * The width is where the uncertainty lives, so the span is shown rather than a
 * single number — and it is always hedged, because it is an estimate.
 */
export function formatWeekSpan(span: {
  readonly fastestWeeks: number
  readonly slowestWeeks: number
}): DisplayString {
  if (span.fastestWeeks === span.slowestWeeks) {
    return `about ${formatWeekCount(span.fastestWeeks)}` as DisplayString
  }
  return `about ${span.fastestWeeks}–${formatWeekCount(span.slowestWeeks)}` as DisplayString
}

export function formatWeekCount(weeks: number): DisplayString {
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}` as DisplayString
}
